import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Alert,
  IconButton,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getLocalChapterInfo,
  getLCLeadership,
  submitElectionOfOfficers,
} from '../../../endpoints/chaptersTs';
import type { LCLeadershipData } from '../../../endpoints/chaptersTs';
import type {
  LocalChapterInfo,
  ElectionSubmitRequest,
  ElectionSubmitMeetingTime,
  ElectionSubmitLeadershipSlot,
} from '../../../types/chapters';
import CountrySelector from '../../../Components/CountrySelector';
import UserSearchModal from '../../../Components/UserSearchModal';
import AlertItem from '../../../Components/AlertItem';
import { useQueryClient } from '@tanstack/react-query';
import {
  days_of_week,
  times_of_day,
  months,
  test_frequecy,
  monthlyTimes,
} from '../../../utils/variables';

const WIZARD_STEPS = [
  'Chapter information',
  'Officers',
  'Project chairs',
  'Review & submit',
];

const OFFICER_START_MIN_DATE = '2026-03-01';

function getOfficerValidationError(
  slots: ElectionSubmitLeadershipSlot[]
): string | null {
  const missing = slots.find((o) => o.user_id && !o.start_date);
  if (missing) return 'Each assigned officer must have a start date.';
  const tooEarly = slots.find(
    (o) => o.user_id && o.start_date && o.start_date < OFFICER_START_MIN_DATE
  );
  if (tooEarly)
    return 'Start date must be on or after March 1, 2026 for each assigned officer.';
  return null;
}

function getProjectChairValidationError(
  slots: ElectionSubmitLeadershipSlot[]
): string | null {
  const missing = slots.find((o) => o.user_id && !o.start_date);
  if (missing) return 'Each assigned project chair must have a start date.';
  const tooEarly = slots.find(
    (o) => o.user_id && o.start_date && o.start_date < OFFICER_START_MIN_DATE
  );
  if (tooEarly)
    return 'Start date must be on or after March 1, 2026 for each assigned project chair.';
  return null;
}

function getOfficerInvalidPositionIds(
  slots: ElectionSubmitLeadershipSlot[]
): string[] {
  return slots
    .filter(
      (o) =>
        o.user_id && (!o.start_date || o.start_date < OFFICER_START_MIN_DATE)
    )
    .map((o) => o.position_id);
}

function getProjectChairInvalidPositionIds(
  slots: ElectionSubmitLeadershipSlot[]
): string[] {
  return slots
    .filter(
      (o) =>
        o.user_id && (!o.start_date || o.start_date < OFFICER_START_MIN_DATE)
    )
    .map((o) => o.position_id);
}

const defaultMeetingFormData: ElectionSubmitMeetingTime = {
  midday: 'am',
  time_of_day: '12:00',
  day_of_week_id: '',
  frequency_timing_id: '',
  frequency_id: '',
  start_month_id: '',
  end_month_id: '',
};

function meetingTimeDisplayLabel(mt: ElectionSubmitMeetingTime): string {
  const day =
    (days_of_week.find((d) => d.id === mt.day_of_week_id)?.name ??
      mt.day_of_week_id) ||
    '?';
  const time = `${mt.time_of_day || '?'} ${(mt.midday || 'am').toUpperCase()}`;
  const freq = test_frequecy.find((f) => f.id === mt.frequency_id)?.name ?? '';
  const timing =
    monthlyTimes.find((t) => t.id === mt.frequency_timing_id)?.name ?? '';
  const startM =
    (months.find((m) => m.id === mt.start_month_id)?.name ??
      mt.start_month_id) ||
    '?';
  const endM =
    (months.find((m) => m.id === mt.end_month_id)?.name ?? mt.end_month_id) ||
    '?';
  const when = startM === endM ? startM : `${startM} – ${endM}`;
  const freqLine =
    freq && timing ? `${freq}, ${timing} week` : freq || timing || '';
  return freqLine
    ? `${day}, ${time} • ${freqLine} • ${when}`
    : `${day}, ${time}`;
}

export default function ElectionOfOfficersWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);

  const [chapterInfo, setChapterInfo] = useState<{
    information: string;
    postal_code: string;
    country_id: string;
    country_name: string;
  }>({ information: '', postal_code: '', country_id: '', country_name: '' });
  const [meetingTimes, setMeetingTimes] = useState<ElectionSubmitMeetingTime[]>(
    []
  );
  const [officers, setOfficers] = useState<
    Record<string, { user_id: string; user_name: string; start_date: string }>
  >({});
  const [chairs, setChairs] = useState<
    Record<string, { user_id: string; user_name: string; start_date: string }>
  >({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [editingMeetingIndex, setEditingMeetingIndex] = useState<number | null>(
    null
  );
  const [meetingFormData, setMeetingFormData] =
    useState<ElectionSubmitMeetingTime>(defaultMeetingFormData);

  const { data: info, isFetching: loadingInfo } = useApiGet<LocalChapterInfo>(
    ['local-chapter-info', id],
    getLocalChapterInfo,
    { enabled: !!id, retry: 1 },
    { chapterId: id! }
  );

  const { data: leadership, isFetching: loadingLeadership } =
    useApiGet<LCLeadershipData>(
      ['lc-leadership', id],
      getLCLeadership,
      { enabled: !!id, retry: 1 },
      { id: id! }
    );

  useEffect(() => {
    if (info) {
      setChapterInfo({
        information: info.information ?? '',
        postal_code: info.postal_code ?? '',
        country_id: info.country_id ?? '',
        country_name: '',
      });
      if (info.meeting_times && info.meeting_times.length > 0) {
        setMeetingTimes(
          info.meeting_times.map((mt) => ({
            midday: mt.midday,
            time_of_day: mt.time_of_day,
            day_of_week_id: mt.day_of_week_id,
            frequency_timing_id: mt.frequency_timing_id || '',
            frequency_id: mt.frequency_id || '',
            start_month_id: mt.start_month_id || '',
            end_month_id: mt.end_month_id || '',
          }))
        );
      }
    }
  }, [info]);

  const { mutate: submit, isPending: submitting } = useApiSend(
    (data: ElectionSubmitRequest) => submitElectionOfOfficers(id!, data),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['election-of-officers-status', id],
      });
      queryClient.invalidateQueries({ queryKey: ['lc-leadership', id] });
      navigate(`/local-chapters/${id}?tab=leadership`, { replace: true });
    },
    (err: any) => {
      setSubmitError(err?.message || err?.details || 'Submit failed');
    },
    [
      ['election-of-officers-status', id],
      ['lc-leadership', id],
    ]
  );

  const handleNext = () => {
    setStepError(null);
    if (activeStep === 1) {
      const payload = buildPayload();
      const err = getOfficerValidationError(payload.officers);
      if (err) {
        setStepError(err);
        return;
      }
    }
    if (activeStep === 2) {
      const payload = buildPayload();
      const err = getProjectChairValidationError(payload.project_chairs);
      if (err) {
        setStepError(err);
        return;
      }
    }
    setActiveStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
  };
  const handleBack = () => {
    setStepError(null);
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleOpenMeetingDialog = (index?: number) => {
    if (index !== undefined && meetingTimes[index]) {
      setEditingMeetingIndex(index);
      setMeetingFormData({ ...defaultMeetingFormData, ...meetingTimes[index] });
    } else {
      setEditingMeetingIndex(null);
      setMeetingFormData(defaultMeetingFormData);
    }
    setMeetingDialogOpen(true);
  };

  const handleCloseMeetingDialog = () => {
    setMeetingDialogOpen(false);
    setEditingMeetingIndex(null);
    setMeetingFormData(defaultMeetingFormData);
  };

  const handleSubmitMeeting = () => {
    const full: ElectionSubmitMeetingTime = {
      midday: meetingFormData.midday || 'am',
      time_of_day: meetingFormData.time_of_day || '12:00',
      day_of_week_id: meetingFormData.day_of_week_id,
      frequency_timing_id: meetingFormData.frequency_timing_id || '',
      frequency_id: meetingFormData.frequency_id || '',
      start_month_id: meetingFormData.start_month_id || '',
      end_month_id: meetingFormData.end_month_id || '',
    };
    if (editingMeetingIndex !== null) {
      setMeetingTimes((prev) => {
        const next = [...prev];
        next[editingMeetingIndex] = full;
        return next;
      });
    } else {
      setMeetingTimes((prev) => [...prev, full]);
    }
    handleCloseMeetingDialog();
  };

  const handleDeleteMeeting = (index: number) => {
    setMeetingTimes((prev) => prev.filter((_, i) => i !== index));
  };

  const buildPayload = (): ElectionSubmitRequest => {
    const officersList: ElectionSubmitLeadershipSlot[] = leadership
      ? leadership.officers
          .sort((a, b) => a.order - b.order)
          .map((o) => {
            const slot = officers[o.position_id];
            return {
              position_id: o.position_id,
              user_id: slot?.user_id ?? '',
              start_date: slot?.start_date ?? '',
            };
          })
      : [];
    const chairsList: ElectionSubmitLeadershipSlot[] = leadership
      ? leadership.project_chairs.map((c) => {
          const slot = chairs[c.position_id];
          return {
            position_id: c.position_id,
            user_id: slot?.user_id ?? '',
            start_date: slot?.start_date ?? '',
          };
        })
      : [];

    return {
      chapter_info: {
        information: chapterInfo.information || null,
        postal_code: chapterInfo.postal_code || null,
        country_id: chapterInfo.country_id || null,
      },
      meeting_times: meetingTimes.length > 0 ? meetingTimes : undefined,
      officers: officersList,
      project_chairs: chairsList,
    };
  };

  const handleSubmit = () => {
    setSubmitError(null);
    const payload = buildPayload();
    const officerErr = getOfficerValidationError(payload.officers);
    if (officerErr) {
      setSubmitError(officerErr);
      return;
    }
    const chairErr = getProjectChairValidationError(payload.project_chairs);
    if (chairErr) {
      setSubmitError(chairErr);
      return;
    }
    submit(payload);
  };

  if (!id) {
    return (
      <Box p={2}>
        <Typography color="error">Missing chapter ID</Typography>
      </Box>
    );
  }

  if (loadingInfo || loadingLeadership) {
    return (
      <Box p={2}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Chapter description"
                value={chapterInfo.information}
                onChange={(e) =>
                  setChapterInfo((prev) => ({
                    ...prev,
                    information: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal code"
                value={chapterInfo.postal_code}
                onChange={(e) =>
                  setChapterInfo((prev) => ({
                    ...prev,
                    postal_code: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CountrySelector
                value={chapterInfo.country_id}
                handleChange={(opt) =>
                  setChapterInfo((prev) => ({
                    ...prev,
                    country_id: opt?.id ?? '',
                    country_name: opt?.name ?? '',
                  }))
                }
                required={false}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mt: 1, mb: 1 }}
              >
                Meeting times
              </Typography>
              {meetingTimes.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {meetingTimes.map((mt, i) => (
                    <Box
                      key={i}
                      sx={{
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {meetingTimeDisplayLabel(mt)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenMeetingDialog(i)}
                        aria-label="Edit meeting time"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMeeting(i)}
                        aria-label="Delete meeting time"
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No meeting times added yet.
                </Typography>
              )}
              <Button
                startIcon={<Add />}
                variant="outlined"
                size="small"
                onClick={() => handleOpenMeetingDialog()}
                sx={{ mt: 1 }}
              >
                {meetingTimes.length > 0
                  ? 'Add another meeting time'
                  : 'Add meeting time'}
              </Button>
            </Grid>
          </Grid>
        );
      case 1: {
        const payload1 = buildPayload();
        const invalidOfficerIds = getOfficerInvalidPositionIds(
          payload1.officers
        );
        return (
          <Grid container spacing={2}>
            {leadership?.officers
              .sort((a, b) => a.order - b.order)
              .map((o) => (
                <Grid item xs={12} key={o.position_id}>
                  <Card
                    variant="outlined"
                    sx={
                      stepError && invalidOfficerIds.includes(o.position_id)
                        ? {
                            borderColor: 'error.main',
                            borderWidth: 2,
                            boxShadow: 1,
                            bgcolor: 'error.light',
                          }
                        : undefined
                    }
                  >
                    <CardContent>
                      <Typography fontWeight="medium">{o.position}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Current: {o.name || '—'}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          mt: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <OfficerSlotEditor
                          chapterId={id}
                          positionId={o.position_id}
                          value={officers[o.position_id]}
                          onChange={(v) =>
                            setOfficers((prev) => ({
                              ...prev,
                              [o.position_id]: v,
                            }))
                          }
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        );
      }
      case 2: {
        const payload2 = buildPayload();
        const invalidChairIds = getProjectChairInvalidPositionIds(
          payload2.project_chairs
        );
        return (
          <Grid container spacing={2}>
            {leadership?.project_chairs.map((c) => (
              <Grid item xs={12} key={c.position_id}>
                <Card
                  variant="outlined"
                  sx={
                    stepError && invalidChairIds.includes(c.position_id)
                      ? {
                          borderColor: 'error.main',
                          borderWidth: 2,
                          boxShadow: 1,
                          bgcolor: 'error.light',
                        }
                      : undefined
                  }
                >
                  <CardContent>
                    <Typography fontWeight="medium">{c.position}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Current: {c.name || '—'}
                    </Typography>
                    <Box
                      sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}
                    >
                      <OfficerSlotEditor
                        chapterId={id}
                        positionId={c.position_id}
                        value={chairs[c.position_id]}
                        onChange={(v) =>
                          setChairs((prev) => ({ ...prev, [c.position_id]: v }))
                        }
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        );
      }
      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Summary</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review your entries below. Submit to complete.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Chapter information
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {chapterInfo.information || '—'}
              </Typography>
              <Typography variant="body2">
                Postal code: {chapterInfo.postal_code || '—'}
              </Typography>
              <Typography variant="body2">
                Country:{' '}
                {chapterInfo.country_name || chapterInfo.country_id || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Meeting times
              </Typography>
              {meetingTimes.length > 0 ? (
                meetingTimes.map((mt, i) => (
                  <Typography key={i} variant="body2">
                    {meetingTimeDisplayLabel(mt)}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2">None recorded</Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Officers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Per the constitution, the seven elected officer positions
                (President, Vice President, Recording Secretary, Corresponding
                Secretary, Treasurer, Chaplain, Guard) remain in effect until
                April 30 of the current year if not reported. Other officer
                positions not reported are vacated as of the report date.
              </Typography>
              {leadership?.officers
                .sort((a, b) => a.order - b.order)
                .map((o) => {
                  const slot = officers[o.position_id];
                  const name = slot?.user_name || '';
                  const date = slot?.start_date || '';
                  const line =
                    slot?.user_id && name
                      ? `${name}${date ? ` – ${date}` : ''}`
                      : 'Unassigned';
                  return (
                    <Typography key={o.position_id} variant="body2">
                      {o.position}: {line}
                    </Typography>
                  );
                })}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Project chairs
              </Typography>
              {leadership?.project_chairs.map((c) => {
                const slot = chairs[c.position_id];
                const name = slot?.user_name || '';
                const date = slot?.start_date || '';
                const line =
                  slot?.user_id && name
                    ? `${name}${date ? ` – ${date}` : ''}`
                    : 'Unassigned';
                return (
                  <Typography key={c.position_id} variant="body2">
                    {c.position}: {line}
                  </Typography>
                );
              })}
            </Grid>
            {submitError && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setSubmitError(null)}>
                  {submitError}
                </Alert>
              </Grid>
            )}
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>
        Report This Year&apos;s Officers
      </Typography>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ py: 2 }}>
        {WIZARD_STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Card sx={{ mb: 2 }}>
        <CardContent>{renderStepContent()}</CardContent>
      </Card>
      {(activeStep === 1 || activeStep === 2) && stepError && (
        <Box sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={stepError}
            onClose={() => setStepError(null)}
            keepOpen={true}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        <Box>
          {activeStep === WIZARD_STEPS.length - 1 ? (
            <LoadingButton
              loading={submitting}
              variant="contained"
              onClick={handleSubmit}
            >
              Submit
            </LoadingButton>
          ) : (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          )}
        </Box>
      </Box>

      <Dialog
        open={meetingDialogOpen}
        onClose={handleCloseMeetingDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingMeetingIndex !== null
            ? 'Edit Meeting Time'
            : 'Add Meeting Time'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Day of Week</InputLabel>
                <Select
                  value={meetingFormData.day_of_week_id}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      day_of_week_id: e.target.value,
                    })
                  }
                  label="Day of Week"
                >
                  {days_of_week.map((day) => (
                    <MenuItem key={day.id} value={day.id}>
                      {day.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Time of Day</InputLabel>
                <Select
                  value={meetingFormData.time_of_day}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      time_of_day: e.target.value,
                    })
                  }
                  label="Time of Day"
                >
                  {times_of_day.map((time) => (
                    <MenuItem key={time.id} value={time.id}>
                      {time.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>AM/PM</InputLabel>
                <Select
                  value={meetingFormData.midday}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      midday: e.target.value,
                    })
                  }
                  label="AM/PM"
                >
                  <MenuItem value="am">AM</MenuItem>
                  <MenuItem value="pm">PM</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={meetingFormData.frequency_id || ''}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      frequency_id: e.target.value,
                    })
                  }
                  label="Frequency"
                >
                  {test_frequecy.map((freq) => (
                    <MenuItem key={freq.id} value={freq.id}>
                      {freq.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency Timing</InputLabel>
                <Select
                  value={meetingFormData.frequency_timing_id || ''}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      frequency_timing_id: e.target.value,
                    })
                  }
                  label="Frequency Timing"
                >
                  {monthlyTimes.map((timing) => (
                    <MenuItem key={timing.id} value={timing.id}>
                      {timing.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Start Month</InputLabel>
                <Select
                  value={meetingFormData.start_month_id || ''}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      start_month_id: e.target.value,
                    })
                  }
                  label="Start Month"
                >
                  {months.map((month) => (
                    <MenuItem key={month.id} value={month.id}>
                      {month.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>End Month</InputLabel>
                <Select
                  value={meetingFormData.end_month_id || ''}
                  onChange={(e) =>
                    setMeetingFormData({
                      ...meetingFormData,
                      end_month_id: e.target.value,
                    })
                  }
                  label="End Month"
                >
                  {months.map((month) => (
                    <MenuItem key={month.id} value={month.id}>
                      {month.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMeetingDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitMeeting}
            variant="contained"
            disabled={
              !meetingFormData.day_of_week_id ||
              !meetingFormData.frequency_id ||
              !meetingFormData.frequency_timing_id ||
              !meetingFormData.start_month_id ||
              !meetingFormData.end_month_id
            }
          >
            {editingMeetingIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function OfficerSlotEditor({
  chapterId,
  positionId,
  value,
  onChange,
}: {
  chapterId: string;
  positionId: string;
  value?: { user_id: string; user_name: string; start_date: string };
  onChange: (v: {
    user_id: string;
    user_name: string;
    start_date: string;
  }) => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const user_id = value?.user_id ?? '';
  const user_name = value?.user_name ?? '';
  const start_date = value?.start_date ?? '';

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setModalOpen(true)}
      >
        {user_name || 'Unassigned'}
      </Button>
      <TextField
        size="small"
        label="Start date"
        type="date"
        value={start_date}
        onChange={(e) =>
          onChange({
            user_id,
            user_name,
            start_date: e.target.value,
          })
        }
        InputLabelProps={{ shrink: true }}
        inputProps={{
          'aria-label': 'Start date',
          min: OFFICER_START_MIN_DATE,
        }}
      />
      <UserSearchModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        userObjectRet={true}
        setUser={(user) => {
          onChange({
            user_id: user.id,
            user_name: user.full_name ?? '',
            start_date,
          });
          setModalOpen(false);
        }}
        chapter={chapterId}
        statusFilter="active"
      />
    </>
  );
}
